import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Quote, Trophy, ArrowUpRight } from "lucide-react";

const WINNERS = [
  {
    metric: "90 hrs",
    metricLabel: "saved per month · 22 real-time reports",
    caseSlug: "swiggy-payment-reconciliation",
    industry: "Food-Tech Unicorn · FinOps",
    quote:
      "Techovarya turned a three-day manual reconciliation cycle into a real-time system. What used to consume our finance team every month now runs in the background — accurately, across every line of business.",
    name: "Finance Operations Lead",
    company: "Swiggy",
    initials: "SW",
    featured: true,
    badge: "UNICORN",
  },
  {
    metric: "50%",
    metricLabel: "operational efficiency gain",
    caseSlug: "gallop-brush-erp",
    industry: "Manufacturing",
    quote:
      "Techovarya replaced years of spreadsheets and disconnected tools with an ERP that finally matches how the factory actually runs. Inventory, orders, and dispatch now speak the same language.",
    name: "Operations Director",
    company: "Gallop Brush Manufacturing",
    initials: "GB",
  },
  {
    metric: "66%",
    metricLabel: "faster pick-and-pack",
    caseSlug: "shipolog-ai-fulfillment",
    industry: "E-commerce & Fulfillment",
    quote:
      "AI product mapping and barcode scanning transformed our fulfillment floor. Onboarding new hires used to take a week — now they're productive on day one.",
    name: "Head of Fulfillment",
    company: "Shipolog",
    initials: "SH",
  },
  {
    metric: "10x",
    metricLabel: "faster prospect research",
    caseSlug: "ai-sales-content-technology",
    industry: "SalesTech / MarTech",
    quote:
      "Their retrieval and multi-model routing turned an unpredictable AI prototype into a feature our customers actually rely on. The engineering rigor was the difference.",
    name: "VP of Product",
    company: "SalesTech SaaS",
    initials: "ST",
  },
  {
    metric: "Zero",
    metricLabel: "manual reconciliation",
    caseSlug: "logistics-freight-technology",
    industry: "Logistics",
    quote:
      "They understood freight before they wrote code. That shows up in every workflow we shipped — dispatch, exceptions, carrier integrations. It just works.",
    name: "Head of Operations",
    company: "Freight Platform",
    initials: "FP",
  },
];

const Card = ({ w, i }) => (
  <motion.div
    data-testid={`testimonial-card-${i}`}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.6, delay: (i % 2) * 0.1 }}
    className={`group relative bg-[#0A0A0A] hover:bg-[#111] transition-colors border border-white/10 hover:border-[#FF5F1F]/40 p-6 md:p-8 flex flex-col gap-6 ${
      w.featured ? "md:col-span-2" : "md:col-span-1"
    }`}
  >
    {/* Winner chip */}
    <div className="flex items-start justify-between">
      <div className="inline-flex items-center gap-2 flex-wrap">
        <div className="inline-flex items-center gap-2 px-2.5 py-1.5 bg-[#FF5F1F] text-black">
          <Trophy className="w-3.5 h-3.5" strokeWidth={2.5} />
          <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em]">
            Winner
          </span>
        </div>
        {w.badge && (
          <div className="inline-flex items-center gap-1 px-2 py-1.5 border border-[#FF5F1F] text-[#FF5F1F]">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M12 2l2.4 4.9L20 8l-4 3.9.9 5.5L12 14.8 7.1 17.4 8 11.9 4 8l5.6-1.1L12 2z"/></svg>
            <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em]">
              {w.badge}
            </span>
          </div>
        )}
      </div>
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 text-right">
        {w.industry}
      </span>
    </div>

    {/* Big metric */}
    <div className={`flex ${w.featured ? "flex-row items-baseline gap-6" : "flex-col gap-1"}`}>
      <div
        className={`font-headings font-black text-[#FF5F1F] leading-none tracking-[-0.03em] ${
          w.featured ? "text-[96px] md:text-[144px]" : "text-6xl md:text-7xl"
        }`}
      >
        {w.metric}
      </div>
      <div className="font-mono text-[11px] md:text-xs uppercase tracking-[0.2em] text-zinc-400 max-w-[220px]">
        {w.metricLabel}
      </div>
    </div>

    {/* Quote */}
    <div className="relative pl-5 border-l-2 border-[#FF5F1F]/60 flex-1">
      <Quote
        className="absolute -left-1 -top-2 w-4 h-4 text-[#FF5F1F] bg-[#0A0A0A]"
        strokeWidth={2}
      />
      <p className={`text-zinc-200 leading-relaxed ${w.featured ? "text-lg md:text-xl" : "text-base"}`}>
        {w.quote}
      </p>
    </div>

    {/* Attribution */}
    <div className="mt-auto pt-5 border-t border-white/5 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-11 h-11 shrink-0 border border-white/15 bg-[#050505] flex items-center justify-center font-headings text-sm font-black text-[#FF5F1F] tracking-tight">
          {w.initials}
        </div>
        <div className="min-w-0">
          <div className="font-headings text-sm md:text-base font-bold text-white leading-tight truncate">
            {w.name}
          </div>
          <div className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.15em] text-zinc-500 truncate">
            {w.company}
          </div>
        </div>
      </div>
      <Link
        to={`/work/${w.caseSlug}`}
        data-testid={`testimonial-case-link-${i}`}
        className="shrink-0 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400 hover:text-[#FF5F1F] transition-colors"
      >
        Case Study
        <ArrowUpRight className="w-3 h-3" />
      </Link>
    </div>
  </motion.div>
);

export const Testimonials = () => {
  return (
    <section
      id="wins"
      data-testid="testimonials-section"
      className="relative bg-[#050505] py-24 md:py-32 border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-8 bg-[#FF5F1F]" />
              <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#FF5F1F]">
                Client Wins
              </span>
            </div>
            <h2 className="font-headings text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-white">
              Where Engineering Shows Up in <span className="text-[#FF5F1F]">Real Business Wins.</span>
            </h2>
          </div>
          <p className="text-sm md:text-base text-zinc-400 max-w-md leading-relaxed">
            The measure of software is not features shipped — it is what changes for the business.
            A few of the outcomes we&rsquo;re most proud of.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {WINNERS.map((w, i) => (
            <Card key={i} w={w} i={i} />
          ))}
        </div>

        <div className="mt-14 md:mt-16 border-t border-white/10 pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="font-mono text-[10px] md:text-xs uppercase tracking-[0.2em] text-zinc-500">
            Every win begins with a focused engineering conversation.
          </p>
          <Link
            to="/work"
            data-testid="testimonials-all-work-cta"
            className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-white hover:text-[#FF5F1F] transition-colors"
          >
            See All Client Work
            <ArrowUpRight className="w-3.5 h-3.5 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
};
